import { ArrowLeft, FileText, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Markdown from '../components/Markdown';
import obchodniPodminky from '../content/legal/obchodni-podminky.md?raw';
import odstoupeniOdSmlouvy from '../content/legal/odstoupeni-od-smlouvy.md?raw';
import ochranaOsobnichUdaju from '../content/legal/ochrana-osobnich-udaju.md?raw';
import reklamacniRad from '../content/legal/reklamacni-rad.md?raw';

/** Klíč právního dokumentu → nadpis stránky. */
const DOCS = {
  'obchodni-podminky': 'Obchodní podmínky',
  'odstoupeni-od-smlouvy': 'Odstoupení od smlouvy',
  'ochrana-osobnich-udaju': 'Ochrana osobních údajů',
  'reklamacni-rad': 'Reklamační řád',
} as const;

export type LegalDoc = keyof typeof DOCS;

/** Klíč dokumentu → zdrojový markdown (importovaný jako text z content/legal). */
const SOURCES: Record<LegalDoc, string> = {
  'obchodni-podminky': obchodniPodminky,
  'odstoupeni-od-smlouvy': odstoupeniOdSmlouvy,
  'ochrana-osobnich-udaju': ochranaOsobnichUdaju,
  'reklamacni-rad': reklamacniRad,
};

/** Odstraní úvodní nadpis H1 (název dokumentu), který už zobrazuje hlavička stránky. */
function stripLeadingH1(md: string): string {
  // Ošetří i případný BOM (U+FEFF) na začátku souboru.
  return md.replace(/^\uFEFF?\s*#\s+.*(?:\r?\n)+/, '');
}

/**
 * Veřejná stránka právního dokumentu. Renderuje se z markdownu v `content/legal/`
 * přes komponentu Markdown.
 */
export default function Legal({ doc }: { doc: LegalDoc }) {
  const title = DOCS[doc];
  const source = stripLeadingH1(SOURCES[doc]);
  return (
    <div className="grid-paper min-h-[70vh] pb-16">
      <main className="mx-auto max-w-2xl px-4 py-6">
        <Link
          to="/"
          className="focus-ring inline-flex h-11 items-center gap-1.5 rounded-xl px-2 text-sm font-medium text-ink-soft transition hover:text-ink"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          Zpět do katalogu
        </Link>

        <div className="mt-4 flex items-center gap-3">
          <span className="board grid h-12 w-12 flex-none place-items-center rounded-xl text-chalk">
            <FileText className="h-6 w-6" strokeWidth={2} />
          </span>
          <h1 className="text-2xl md:text-3xl">{title}</h1>
        </div>

        <article className="mt-6 rounded-card border border-line bg-card p-6 leading-relaxed text-ink shadow-card">
          <Markdown source={source} highlightPlaceholders />
        </article>

        <p className="mt-6 flex items-center gap-2 text-sm text-ink-soft">
          <Mail className="h-4 w-4 flex-none" strokeWidth={2} />
          Dotaz k dokumentu? Napište na{' '}
          <a href="mailto:pf.kabinet.napadu@gmail.com" className="font-semibold text-board">
            pf.kabinet.napadu@gmail.com
          </a>
        </p>
      </main>
    </div>
  );
}
